import { Children, cloneElement, forwardRef, isValidElement, type AnchorHTMLAttributes, type ComponentPropsWithoutRef, type MouseEvent, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TARGET_SIZE = "min-h-11 min-w-11 lg:min-h-6 lg:min-w-6";
// The links are `inline-flex`, which honours min-height/min-width. A pseudo-element hit area was
// tried to survive an authored `inline` display, but absolutely positioned 44px layers overlap
// neighbouring links and steal their clicks, so the contract is instead: do not set a plain
// `inline` or `contents` display on these components (a `hidden`/`flex` choice is fine).
const LINK_STYLE = "inline-flex items-center rounded-sm text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-[var(--focus-ring-width)] focus-visible:ring-ring focus-visible:ring-offset-2";

/**
 * A self-sizing action. `asChild` applies the target to the child itself, without a button wrapper.
 * A disabled `asChild` action is usually a link, and `disabled` means nothing on an anchor, so it is
 * made inert here instead: `aria-disabled`, out of the tab order, and activation prevented.
 */
export const TouchTargetButton = forwardRef<HTMLButtonElement, ButtonProps>(({ className, asChild, disabled, onClick, ...props }, ref) => {
  if (asChild && disabled) {
    // Radix Slot lets the child's own props win over the ones passed here, so a child `tabIndex={0}`
    // or `onClick={track}` would stay live. Override on the final child itself instead.
    const only = Children.toArray(props.children)[0];
    const child = isValidElement(only)
      ? cloneElement(only as ReactElement<Record<string, unknown>>, {
        "aria-disabled": "true",
        tabIndex: -1,
        onClick: (event: MouseEvent) => event.preventDefault(),
      })
      : only;
    return (
      <Button
        {...props}
        asChild
        ref={ref}
        className={cn(className, TARGET_SIZE, "pointer-events-none opacity-50")}
      >
        {child}
      </Button>
    );
  }
  return <Button {...props} asChild={asChild} disabled={disabled} onClick={onClick} ref={ref} className={cn(className, TARGET_SIZE)} />;
});
TouchTargetButton.displayName = "TouchTargetButton";

/** An in-app link owns its target dimensions; responsive display/visibility stays with its author, who must not set a plain `inline` or `contents` display (min dimensions would be ignored). */
export const TouchTargetLink = forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<typeof Link>>(
  ({ className, to, ...props }, ref) => {
    // A nullish or blank `to` resolves to the current route (strictNullChecks is off, so `to={maybePath}` type-checks).
    // The object form (`{ pathname, search, hash }`) resolves the same way when every field is absent or blank.
    const usable = (text: unknown) => typeof text === "string" && text.trim().length > 0;
    const blank = to === null || to === undefined
      || (typeof to === "string" && to.trim().length === 0)
      || (typeof to === "object" && !usable(to.pathname) && !usable(to.search) && !usable(to.hash));
    if (blank) throw new Error("TouchTargetLink needs a non-empty `to` destination.");
    return <Link {...props} to={to} ref={ref} className={cn(LINK_STYLE, className, TARGET_SIZE)} />;
  },
);
TouchTargetLink.displayName = "TouchTargetLink";

/** Native links (external destinations/downloads) use the same explicit target contract. */
export const TouchTargetAnchor = forwardRef<HTMLAnchorElement, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { href: string }>(
  ({ className, href, ...props }, ref) => {
    // Without an href an `<a>` has no link semantics, focus or activation (strictNullChecks is off, so `href={maybe}` type-checks).
    if (typeof href !== "string" || href.trim().length === 0) {
      throw new Error("TouchTargetAnchor needs a non-empty href; use a button for an action without a destination.");
    }
    // eslint-disable-next-line jsx-a11y/anchor-has-content -- children arrive through the spread props; the rule cannot see them.
    return <a {...props} href={href} ref={ref} className={cn(LINK_STYLE, className, TARGET_SIZE)} />;
  },
);
TouchTargetAnchor.displayName = "TouchTargetAnchor";
